import React, { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, AlertTriangle, Download, BarChart3, FileText, Users, ListChecks, User, ArrowRight, Building2, Info } from "lucide-react";
import { cmaService } from "../services/cmaService";
import { formatApiErrorDetail, api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LanguageContext";
import { track } from "../lib/analytics";
import { CMA_AREAS } from "../data/demo";
import { Eyebrow, Demo } from "../components/Bits";

const aed = (n) => "AED " + Number(n).toLocaleString();
const TAB_ICONS = [BarChart3, FileText, Users, ListChecks, BarChart3, User];

// Prefilled subject properties. Labels are neutral descriptors only — the
// confidence level is discovered when the result returns, never announced here.
const EXAMPLES = [
  { id: "marina_vista", label: "Marina Vista · Emaar Beachfront", sub: "2BR · 1,250 sqft · AED 2.40M",
    form: { area: "Emaar Beachfront", building: "Marina Vista", bedrooms: 2, property_type: "Apartment", size_sqft: 1250, asking_price: 2400000 } },
  { id: "marina_area", label: "Dubai Marina", sub: "2BR · 1,180 sqft · AED 1.85M",
    form: { area: "Dubai Marina", building: "", bedrooms: 2, property_type: "Apartment", size_sqft: 1180, asking_price: 1850000 } },
  { id: "jvc", label: "Jumeirah Village Circle", sub: "2BR · 1,100 sqft · AED 1.20M",
    form: { area: "Jumeirah Village Circle", building: "", bedrooms: 2, property_type: "Apartment", size_sqft: 1100, asking_price: 1200000 } },
];

export default function BrokerCMA() {
  const { t } = useLang();
  const { user } = useAuth();
  const nav = useNavigate();
  const tabs = t("broker.tabs");
  const R = t("broker.res");
  const F = t("broker.form");
  const [tab, setTab] = useState(0);
  const [form, setForm] = useState({ area: CMA_AREAS[0], building: "", bedrooms: 2, property_type: "Apartment", size_sqft: 1180, asking_price: 1850000 });
  const [result, setResult] = useState(null);
  const [reportId, setReportId] = useState(null);
  const [notes, setNotes] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const set = (k, num) => (e) => setForm({ ...form, [k]: num ? Number(e.target.value) : e.target.value });

  // Reopen a saved report from ?report=<id> — reload full comparables + notes.
  useEffect(() => {
    const rid = searchParams.get("report");
    if (!rid || !user) return;
    (async () => {
      setLoading(true); setError("");
      try {
        const data = await cmaService.getReport(rid);
        setForm({ area: data.input.area, building: data.input.building || "", bedrooms: data.input.bedrooms || 2,
                  property_type: data.input.property_type || "Apartment", size_sqft: data.input.size_sqft, asking_price: data.input.asking_price });
        setResult(data);
        setReportId(data.report_id);
        setNotes(data.notes || "");
        setTab(0);
        track("cma_reopened", { report_id: rid });
      } catch (e) {
        setError(t("common.error"));
      } finally { setLoading(false); }
    })();
    // eslint-disable-next-line
  }, [searchParams, user]);

  const generate = async (override) => {
    const f = override && !override.nativeEvent ? override : form;
    setLoading(true); setError(""); setResult(null); setReportId(null); setNotes("");
    if (searchParams.get("report")) setSearchParams({});
    try {
      const params = { ...f, bedrooms: f.bedrooms || undefined };
      const data = await cmaService.preview(params);
      setResult(data);
      track("cma_run", { area: f.area, insufficient: !!data.insufficient });
      if (user && !data.insufficient) {
        try { const rep = await cmaService.createReport(f); setReportId(rep.report_id); } catch { /* save optional */ }
      }
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || t("common.error"));
    } finally { setLoading(false); }
  };

  const runExample = (ex) => {
    setForm(ex.form);
    track("cma_example_run", { example: ex.id });
    generate(ex.form);
  };

  const pickBuilding = (building) => {
    const next = { ...form, building };
    setForm(next);
    track("cma_refine_building", { building });
    generate(next);
  };

  const saveNotes = async () => {
    if (!reportId) return;
    setSavingNotes(true);
    try {
      await cmaService.updateNotes(reportId, notes);
      track("cma_notes_saved", { report_id: reportId });
      toast.success(t("broker.notes_saved"));
    } catch (e) {
      toast.error(t("common.error"));
    } finally { setSavingNotes(false); }
  };

  const doExport = () => {
    if (!reportId) return;
    track("cma_export", { report_id: reportId });
    window.open(cmaService.exportUrl(reportId), "_blank");
  };

  return (
    <div className="max-w-[1400px] mx-auto px-5 lg:px-8 py-12" data-testid="broker-cma-page">
      <Eyebrow>{t("broker.eyebrow")}</Eyebrow>
      <div className="flex items-center gap-3 mt-2 flex-wrap">
        <h1 className="font-display text-4xl text-white">{t("broker.title")}</h1>
        <span className="text-[11px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/30" data-testid="pilot-tag">{t("broker.pilot_tag")}</span>
      </div>
      <p className="text-white/60 mt-3 max-w-2xl">{t("broker.land_p")}</p>
      <p className="text-white/40 text-xs mt-2 max-w-2xl">{t("broker.no_official")}</p>
      {user && (
        <Link to="/partners/broker/dashboard" data-testid="broker-dash-link" className="mt-4 inline-flex items-center gap-1.5 text-sm text-gold border border-gold/25 rounded-full px-4 py-2 hover:bg-gold/10 transition-colors duration-200">
          {t("broker.dash_link")} <ArrowRight size={14} />
        </Link>
      )}

      {/* Tabs */}
      <div className="flex gap-1 mt-8 border-b border-white/10 overflow-x-auto">
        {tabs.map((tb, i) => {
          const Icon = TAB_ICONS[i];
          return (
            <button key={tb} onClick={() => setTab(i)} data-testid={`broker-tab-${i}`}
              className={`flex items-center gap-1.5 px-4 py-3 text-sm whitespace-nowrap border-b-2 -mb-px transition-colors duration-200 ${tab === i ? "border-gold text-gold" : "border-transparent text-white/60 hover:text-white"}`}>
              <Icon size={15} /> {tb}
            </button>
          );
        })}
      </div>

      {tab !== 0 ? (
        <div className="glass rounded-2xl p-10 mt-6 text-center text-white/50" data-testid="broker-tab-soon">{t("broker.soon")}</div>
      ) : (
        <div className="grid md:grid-cols-12 gap-6 mt-6">
          {/* Form */}
          <div className="md:col-span-4 glass rounded-2xl p-6 self-start" data-testid="cma-form">
            <div className="mb-4 pb-4 border-b border-white/10" data-testid="cma-examples">
              <div className="text-xs uppercase tracking-wider text-white/50">{t("broker.examples_h")}</div>
              <div className="mt-2.5 space-y-2">
                {EXAMPLES.map((ex) => (
                  <button key={ex.id} onClick={() => runExample(ex)} disabled={loading}
                    data-testid={`cma-example-${ex.id}`}
                    className="w-full text-left px-3 py-2 rounded-lg border border-white/10 hover:border-gold/40 hover:bg-gold/5 disabled:opacity-50 transition-colors duration-200">
                    <div className="text-sm text-white">{ex.label}</div>
                    <div className="text-[11px] text-white/45 mt-0.5">{ex.sub}</div>
                  </button>
                ))}
              </div>
              <p className="text-[11px] text-white/35 mt-2.5">{t("broker.examples_note")}</p>
            </div>
            <div className="space-y-3">
              <Field label={F.area}>
                <select value={form.area} onChange={set("area")} data-testid="cma-area" className="w-full bg-input/50 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white outline-none focus:border-gold/40">
                  {CMA_AREAS.map((a) => <option key={a} className="bg-surface">{a}</option>)}
                </select>
              </Field>
              <Field label={F.building}><input value={form.building} onChange={set("building")} data-testid="cma-building" className="cma-in" placeholder="Marina Gate" /></Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label={F.bedrooms}><input type="number" value={form.bedrooms} onChange={set("bedrooms", true)} data-testid="cma-bedrooms" className="cma-in" /></Field>
                <Field label={F.type}>
                  <select value={form.property_type} onChange={set("property_type")} className="w-full bg-input/50 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white outline-none focus:border-gold/40">
                    <option className="bg-surface">Apartment</option><option className="bg-surface">Villa</option><option className="bg-surface">Townhouse</option>
                  </select>
                </Field>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label={F.size}><input type="number" value={form.size_sqft} onChange={set("size_sqft", true)} data-testid="cma-size" className="cma-in" /></Field>
                <Field label={F.price}><input type="number" value={form.asking_price} onChange={set("asking_price", true)} data-testid="cma-price" className="cma-in" /></Field>
              </div>
              <button onClick={() => generate()} disabled={loading} data-testid="cma-generate" className="w-full py-3 rounded-lg bg-gold text-ink font-semibold hover:brightness-110 disabled:opacity-50 flex items-center justify-center gap-2 transition-all duration-200">
                {loading ? <><Loader2 size={16} className="animate-spin" /> {t("common.loading")}</> : t("broker.generate")}
              </button>
              {!user && <p className="text-[11px] text-white/40">{t("broker.sign_in_hint")}</p>}
            </div>
          </div>

          {/* Result */}
          <div className="md:col-span-8">
            {error && <div className="glass rounded-2xl p-6 text-red-400 text-sm" data-testid="cma-error">{error}</div>}
            {!result && !error && <div className="glass rounded-2xl p-10 text-center text-white/40" data-testid="cma-empty">Enter subject details and generate a CMA.</div>}

            {result?.insufficient && (
              <div className="glass rounded-2xl p-8" data-testid="cma-insufficient">
                <div className="flex items-center gap-2 text-gold"><AlertTriangle size={18} /><span className="font-display text-lg">{t("broker.insufficient_h")}</span></div>
                <p className="text-white/60 text-sm mt-3">{result.reason}</p>
                <div className="mt-3 text-xs text-white/40">{R.level}: {result.comparable_level} · {R.count}: {result.comparable_count}</div>
                {result.area_level_notice && <AreaNotice text={result.area_level_notice} suggestions={result.building_suggestions} onPick={pickBuilding} disabled={loading} />}
              </div>
            )}

            {result && !result.insufficient && (
              <div className="space-y-4" data-testid="cma-result">
                {result.area_level_notice && <AreaNotice text={result.area_level_notice} suggestions={result.building_suggestions} onPick={pickBuilding} disabled={loading} />}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <Stat label={R.confidence} value={String(result.confidence || "").replace(/^./, c => c.toUpperCase())} accent />
                  <Stat label={R.level} value={result.comparable_level} />
                  <Stat label={R.count} value={result.comparable_count} />
                  <Stat label={R.median} value={aed(result.median_aed_per_sqft)} />
                  <Stat label={R.p25} value={aed(result.p25_aed_per_sqft)} />
                  <Stat label={R.p75} value={aed(result.p75_aed_per_sqft)} />
                  <Stat label={R.subject} value={aed(result.subject_aed_per_sqft)} />
                  <Stat label={R.deviation} value={`${result.deviation_pct > 0 ? "+" : ""}${result.deviation_pct}%`} accent={Math.abs(result.deviation_pct) > 8} />
                </div>

                {result.confidence_legend && (
                  <div className="flex items-start gap-2 text-xs text-white/55 px-1" data-testid="cma-confidence-legend">
                    <Info size={14} className="text-gold shrink-0 mt-0.5" />
                    <span>{result.confidence_legend}</span>
                  </div>
                )}

                <div className="glass rounded-2xl p-6">
                  <div className="text-xs uppercase tracking-wider text-white/40">{R.range}</div>
                  <div className="font-display text-2xl text-gold mt-1">{aed(result.suggested_price_low)} — {aed(result.suggested_price_high)}</div>
                </div>

                <div className="glass rounded-2xl p-6" data-testid="cma-comps">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2"><span className="font-display text-lg text-white">{R.comps}</span><Demo /></div>
                    {reportId && <button onClick={doExport} data-testid="cma-export" className="inline-flex items-center gap-1.5 text-sm text-gold border border-gold/20 rounded-lg px-3 py-1.5 hover:bg-gold/10 transition-colors duration-200"><Download size={14} /> {R.export} PDF</button>}
                  </div>
                  <div className="space-y-3">
                    {result.comparables.map((c, i) => (
                      <div key={i} className="border-b border-white/5 pb-3">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-white">{c.building} · {c.bedrooms}BR · {c.size_sqft} sqft</span>
                          <span className="text-gold">{aed(c.price)} <span className="text-white/40 text-xs">· {aed(c.aed_per_sqft)}/sqft · {c.date}</span></span>
                        </div>
                        <div className="mt-1.5 flex flex-wrap gap-1.5">
                          <span className="text-[10px] text-white/40 uppercase tracking-wider mr-1">{R.why}:</span>
                          {c.why_selected.map((w) => <span key={w} className="text-[11px] px-2 py-0.5 rounded-full bg-white/5 text-white/60">{w}</span>)}
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="text-[11px] text-white/40 mt-4">{result.disclaimer}</p>
                </div>

                {reportId && (
                  <div className="glass rounded-2xl p-6" data-testid="cma-notes">
                    <div className="text-sm font-semibold text-white mb-3">{t("broker.notes_h")}</div>
                    <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} data-testid="cma-notes-input"
                      placeholder={t("broker.notes_ph")} className="w-full bg-input/50 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-white/40 outline-none focus:border-gold/40 resize-none" />
                    <button onClick={saveNotes} disabled={savingNotes} data-testid="cma-notes-save"
                      className="mt-3 inline-flex items-center gap-1.5 text-sm text-gold border border-gold/20 rounded-lg px-4 py-2 hover:bg-gold/10 disabled:opacity-50 transition-colors duration-200">
                      {savingNotes ? t("common.loading") : t("broker.notes_save")}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
      <HowItWorks />
      <SampleReport />
      <PilotForm />
      <style>{`.cma-in{width:100%;background:rgba(30,40,70,.5);border:1px solid rgba(255,255,255,.1);border-radius:.5rem;padding:.625rem .75rem;font-size:.875rem;color:#fff;outline:none}.cma-in:focus{border-color:rgba(212,175,55,.4)}`}</style>
    </div>
  );
}

const HowItWorks = () => {
  const { t } = useLang();
  return (
    <section className="mt-16" data-testid="broker-how">
      <h2 className="font-display text-2xl text-white mb-5">{t("broker.how_h")}</h2>
      <div className="grid md:grid-cols-3 gap-4">
        {t("broker.how").map((s, i) => (
          <div key={i} className="rounded-2xl bg-surface/60 border border-white/[0.06] p-6">
            <div className="w-8 h-8 rounded-full bg-gold/10 text-gold flex items-center justify-center font-display">{i + 1}</div>
            <div className="font-display text-lg text-white mt-3">{s.h}</div>
            <p className="text-white/55 text-sm mt-1">{s.p}</p>
          </div>
        ))}
      </div>
    </section>
  );
};

const SampleReport = () => {
  const { t } = useLang();
  return (
    <section className="mt-14" data-testid="broker-sample">
      <div className="flex items-center gap-2 mb-4"><h2 className="font-display text-2xl text-white">{t("broker.sample_h")}</h2><Demo /></div>
      <div className="glass rounded-2xl p-6">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[["Confidence", "High"], ["Median AED/sqft", "1,588"], ["P25–P75", "1,540–1,632"], ["Deviation", "+0.9%"]].map(([k, v]) => (
            <div key={k} className="rounded-xl bg-white/[0.03] border border-white/5 p-4"><div className="text-[11px] text-white/40 uppercase tracking-wider">{k}</div><div className="font-display text-lg text-gold mt-1">{v}</div></div>
          ))}
        </div>
        <div className="mt-4 rounded-xl bg-white/[0.02] border border-white/5 p-4 text-sm text-white/60">
          <div className="flex justify-between border-b border-white/5 pb-2"><span>Marina Gate · 2BR · 1,160 sqft</span><span className="text-white">AED 1,810,000</span></div>
          <div className="flex justify-between border-b border-white/5 py-2"><span>Marina Gate · 2BR · 1,205 sqft</span><span className="text-white">AED 1,910,000</span></div>
          <div className="flex justify-between pt-2"><span>Vida · 2BR · 1,240 sqft</span><span className="text-white">AED 1,975,000</span></div>
        </div>
        <p className="text-[11px] text-white/40 mt-3">{t("broker.sample_note")}</p>
      </div>
    </section>
  );
};

const PilotForm = () => {
  const { t } = useLang();
  const F = t("broker.pilot_fields");
  const [form, setForm] = useState({ name: "", agency: "", brn: "", email: "", phone: "", areas: "", feed_format: "", listings_per_month: "", company_website: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/broker/pilot", form);
      setDone(true);
      track("broker_pilot_completed", { agency: form.agency });
      toast.success(t("broker.pilot_ok_p"));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || t("common.error"));
    } finally { setBusy(false); }
  };
  return (
    <section className="mt-14" data-testid="broker-pilot">
      <div className="glass rounded-2xl p-7 max-w-3xl">
        <h2 className="font-display text-2xl text-white">{t("broker.pilot_h")}</h2>
        <p className="text-white/55 text-sm mt-1">{t("broker.pilot_p")}</p>
        {done ? (
          <div className="text-center py-10" data-testid="pilot-success">
            <div className="font-display text-xl text-emerald-400">{t("broker.pilot_ok_h")}</div>
            <p className="text-white/55 text-sm mt-1">{t("broker.pilot_ok_p")}</p>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-5 grid sm:grid-cols-2 gap-3" data-testid="pilot-form" onFocus={() => track("broker_pilot_started")}>
            <input type="text" value={form.company_website} onChange={set("company_website")} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
            <input required placeholder={F.name} value={form.name} onChange={set("name")} data-testid="pilot-name" className="cma-in" />
            <input required placeholder={F.agency} value={form.agency} onChange={set("agency")} data-testid="pilot-agency" className="cma-in" />
            <input placeholder={F.brn} value={form.brn} onChange={set("brn")} data-testid="pilot-brn" className="cma-in" />
            <input required type="email" placeholder={F.email} value={form.email} onChange={set("email")} data-testid="pilot-email" className="cma-in" />
            <input required placeholder={F.phone} value={form.phone} onChange={set("phone")} data-testid="pilot-phone" className="cma-in" />
            <input placeholder={F.areas} value={form.areas} onChange={set("areas")} data-testid="pilot-areas" className="cma-in" />
            <input placeholder={F.feed} value={form.feed_format} onChange={set("feed_format")} data-testid="pilot-feed" className="cma-in" />
            <input placeholder={F.listings} value={form.listings_per_month} onChange={set("listings_per_month")} data-testid="pilot-listings" className="cma-in" />
            <button disabled={busy} type="submit" data-testid="pilot-submit" className="sm:col-span-2 py-3 rounded-lg bg-gold text-ink font-semibold hover:brightness-110 disabled:opacity-50 transition-all duration-200">
              {busy ? t("common.submitting") : t("broker.pilot_submit")}
            </button>
          </form>
        )}
      </div>
    </section>
  );
};

const Field = ({ label, children }) => (
  <div><label className="text-xs text-white/50 mb-1 block">{label}</label>{children}</div>
);
const AreaNotice = ({ text, suggestions, onPick, disabled }) => (
  <div className="glass rounded-xl p-4 border border-gold/25 bg-gold/5 flex items-start gap-3" data-testid="cma-area-notice">
    <Building2 size={18} className="text-gold shrink-0 mt-0.5" />
    <div className="flex-1">
      <p className="text-sm text-white/75">{text}</p>
      {suggestions && suggestions.length > 0 && (
        <div className="mt-2.5 flex flex-wrap gap-2" data-testid="cma-refine-chips">
          {suggestions.map((s) => (
            <button key={s.building} onClick={() => onPick && onPick(s.building)} disabled={disabled}
              data-testid={`cma-refine-${s.building.toLowerCase().replace(/\s+/g, "-")}`}
              className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-gold/30 text-gold hover:bg-gold/15 disabled:opacity-40 transition-colors duration-200">
              {s.building} <span className="text-gold/60">({s.comp_count})</span>
            </button>
          ))}
        </div>
      )}
    </div>
  </div>
);
const Stat = ({ label, value, accent }) => (
  <div className="glass rounded-xl p-4">
    <div className="text-[11px] text-white/40 uppercase tracking-wider">{label}</div>
    <div className={`font-display text-lg mt-1 ${accent ? "text-gold" : "text-white"}`}>{value}</div>
  </div>
);
